import type * as React from 'react'
import { Toaster as SonnerToaster, toast as sonnerToast } from 'sonner'

import { cn } from '../lib/cn'

/* A wrapper over Sonner: apps mount <Toaster /> once and call `toast` from here, never
   `sonner` directly (eslint.app-rules.mjs). Sonner renders in place, not in a portal, so the
   ink island is a wrapper around it, and `theme` stays `dark` to match. */

// Sonner's own stylesheet is unlayered, so it beats the Tailwind classes below: the ink
// reaches the toast through the variables Sonner reads instead.
const INK_TOAST = {
  '--normal-bg': 'hsl(var(--background))',
  '--normal-text': 'hsl(var(--foreground))',
  '--normal-border': 'hsl(var(--border))',
} as React.CSSProperties

// Sonner's 16px at the foot of a phone is where the bottom bar stands: the bar's own
// reservation plus the page gutter clears it. Other sides keep Sonner's default.
const ABOVE_BOTTOM_BAR = { bottom: 'calc(var(--size-bottom-bar) + var(--spacing-content-gutter))' }

export type ToasterTheme = 'light' | 'dark' | 'system'

export interface ToasterProps {
  theme?: ToasterTheme
  /** Position on screen; defaults to bottom right. */
  position?:
    'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'
  className?: string
  /** The app has a bottom bar: on a phone the toasts stand above it instead of over it. */
  clearBottomBar?: boolean
}

export function Toaster({
  theme = 'dark',
  position = 'bottom-right',
  className,
  clearBottomBar = false,
}: ToasterProps) {
  return (
    <div className="contents" data-surface="ink">
      <SonnerToaster
        theme={theme}
        position={position}
        mobileOffset={clearBottomBar ? ABOVE_BOTTOM_BAR : undefined}
        className={cn('toaster group', className)}
        style={INK_TOAST}
        toastOptions={{
          classNames: {
            toast:
              'group toast group-[.toaster]:border-border group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:shadow-lg',
            description: 'group-[.toast]:text-muted-foreground',
            actionButton: 'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground',
            cancelButton: 'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground',
            error: 'group-[.toaster]:text-destructive',
            success: 'group-[.toaster]:text-success',
            warning: 'group-[.toaster]:text-warning',
          },
        }}
      />
    </div>
  )
}

/** Messages are the caller's business: there is no text here. */
export const toast = sonnerToast
